/**
 * use-onboarding.ts — trang "Bắt đầu".
 *   GET  /onboarding/status   tiến độ (kênh, tin trả lời đầu, AI, tự động hoá, nhân viên) + mốc bỏ qua/hoàn thành
 *                             + bước mở gần nhất (`lastStep`) + mốc từng bước (`stepsDone`)
 *   POST /onboarding/dismiss  "Bỏ qua, vào hệ thống"
 *   POST /onboarding/step     bước vừa mở từ trang Bắt đầu (best-effort)
 *
 * Trạng thái tự cập nhật: gọi lại khi tab được focus / mỗi lần mount (quay về trang), và khi nhận sự kiện
 * socket `onboarding:progress` (kênh vừa kết nối / tin trả lời đầu tiên) hoặc `zalo:connected`.
 */
import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { getSocket } from '@/lib/socket'
import type { OnboardingStatus, OnboardingStepNo } from '@/lib/onboarding'

export const onboardingKeys = { status: ['onboarding', 'status'] as const }

/** Sự kiện socket làm tiến độ có thể đổi (phòng tổ chức). */
export const ONBOARDING_REFRESH_EVENTS = ['onboarding:progress', 'zalo:connected'] as const

export function useOnboardingStatus(enabled = true) {
  return useQuery<OnboardingStatus>({
    queryKey: onboardingKeys.status,
    enabled,
    staleTime: 30_000,
    retry: false,
    // Khách quay lại tab (sau OAuth / làm ở tab khác) hay quay về trang → thấy ngay bước đã tick.
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    queryFn: async () => (await api.get<OnboardingStatus>('/onboarding/status')).data,
  })
}

/** Nghe socket và làm mới trạng thái onboarding — chỉ bật cho người đang cần (tránh gọi API thừa). */
export function useOnboardingLiveRefresh(enabled: boolean) {
  const qc = useQueryClient()
  useEffect(() => {
    if (!enabled) return
    const socket = getSocket()
    const refresh = () => void qc.invalidateQueries({ queryKey: onboardingKeys.status })
    for (const ev of ONBOARDING_REFRESH_EVENTS) socket.on(ev, refresh)
    return () => {
      for (const ev of ONBOARDING_REFRESH_EVENTS) socket.off(ev, refresh)
    }
  }, [enabled, qc])
}

export function useDismissOnboarding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ dismissedAt: string | null; completedAt: string | null }>('/onboarding/dismiss')).data,
    onSuccess: (d) => {
      qc.setQueryData<OnboardingStatus>(onboardingKeys.status, (old) => (old ? { ...old, ...d } : old))
    },
  })
}

/** Ghi bước vừa mở (phễu kích hoạt) — lỗi bỏ qua, không làm phiền người dùng. */
export function recordOnboardingStep(step: OnboardingStepNo) {
  void api.post('/onboarding/step', { step }).catch(() => undefined)
}
